import { FC, useContext, useEffect, useRef, useState } from "react";
import {
  App,
  Button,
  Descriptions,
  DescriptionsProps,
  Flex,
  InputNumber,
  ModalProps,
  Spin,
  Tooltip
} from "antd";
import { useTranslation } from "react-i18next";
import { QuotationRequest, Requirement } from "@/types/requirement";
import useRequirementParams from "../../hooks/useRequirementParams";
import RequirementSteps from "../../components/RequirementSteps";
import { Supplier, SupplierLadderPrice } from "@/types/purchase";
import RequirementReview from "../../components/RequirementReview";
import { PageId } from "@/types/page";
import DetailModal from "@/components/DetailModal";
import { useGetDetail } from "./hooks/useGetDetail";
import DetailTitle from "./components/DetailTitle";
import RequirementDesc from "./components/RequirementDesc";
import FinanceTable, { FinanceTableRef } from "./components/FinanceTable";

import { DictContext } from "@/layout/context/DictContext";
import { saleItemUpdate, update } from "@/apis/order";
import { SaleOrderAdd, SaleOrderAddItem } from "@/types/sales";
// import { DictItem } from "@/types/dict";
import { numFormmater } from "../../util";

import { QuestionCircleOutlined } from "@ant-design/icons";
import { quotationRequestUpdate } from "@/apis/quotationRequestItem";
import { divided, minus, multi } from "@/utils/calc";

// 小组件：在窄屏下用 CSS writing-mode 竖排显示数字（更美观、DOM 更少）
const VerticalNumber: FC<{ value: string | number; suffix?: string }> = ({
  value,
  suffix
}) => {
  const str = String(value ?? "");
  return (
    <div className="vertical-number" style={{ display: "inline-block" }}>
      <span className="vertical-number-text">{str}</span>
      {suffix ? <span className="vertical-number-suffix">{suffix}</span> : null}
      <style>{`
        .vertical-number { display: inline-flex; align-items: center; }
        .vertical-number-text { white-space: nowrap; }
        .vertical-number-suffix { margin-left: 6px; font-size: 12px; opacity: 0.9 }

        /* 窄屏时改为竖排，限制高度并居中 */
        @media (max-width: 800px) {
          .vertical-number { display: inline-flex; align-items: center; }
          .vertical-number-text {
            writing-mode: vertical-rl;
            transform: rotate(180deg); /* 使数字从上到下正常阅读 */
            line-height: 1;
            max-height: 48px; /* 限制高度，防止过长 */
            overflow: hidden;
            text-overflow: ellipsis;
            font-size: 14px;
          }
          .vertical-number-suffix { display: block; margin-left: 8px; font-size: 12px }
        }
      `}</style>
    </div>
  );
};

interface RequirementDetailProps extends ModalProps {
  id: string;
  handleOk: (type?: "close" | "del") => void;
  number: string;
  handleCancel: () => void;
  updateCb?: (requirement: Requirement) => void;
}

export interface RequirementColumnSupplier {
  prices: Array<SupplierLadderPrice>;
  supplier: Supplier;
  id: string;
}

const FinanceDetail: FC<RequirementDetailProps> = ({
  id,
  number,
  handleOk,
  handleCancel,
  updateCb,
  ...props
}) => {
  const { t } = useTranslation();
  const { notification, modal } = App.useApp();

  const FTR = useRef<FinanceTableRef>(null);

  //detail：详情，items：详情表
  const [detail, items, loading, { getSubMaterial }] = useGetDetail({
    id,
    number,
    showDel: false
  });

  const { params, pageId } = useRequirementParams(true);

  const [open, setOpen] = useState<boolean>(false);

  const [purchesePrice, setpurchesePrice] = useState<number>(0);
  const [extPrice, setExtPrice] = useState<number>(0);
  const [commission, setCommission] = useState<number>(0);
  const [canEdit, setCanEdit] = useState<boolean>(false);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  // const formRef = useRef<FormInstance<SaleOrderType>>(null);
  // const [maxDisc, setMaxDisc] = useState<number>(0);
  // const [maxBDisc, setMaxBDisc] = useState<number>(0);

  const [editFlag, setEditFlag] = useState<boolean>(false);

  const { currencys } = useContext(DictContext);

  // 响应式：在宽度较窄时切换为移动布局（阈值 <= 800px）
  const [isMobile, setIsMobile] = useState<boolean>(
    typeof window !== "undefined" ? window.innerWidth <= 800 : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 800);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // 因为getSubMaterial在依赖中会导致页面循环渲染
  // 使用 ref 守卫以确保即使 getSubMaterial 在每次渲染时变化，也只在 id 或 pageId 发生变化时调用一次
  const _subMaterialCalledRef = useRef<{ id?: string; pageId?: PageId }>({});
  // 是否处于「已通过态 (财务已通过)
  const isPassed = pageId === PageId.F_PASSED;

  useEffect(() => {
    if (!pageId) return;
    // 如果 id 和 pageId 与上次一致，就不重复调用，避免循环渲染
    if (
      _subMaterialCalledRef.current.id === id &&
      _subMaterialCalledRef.current.pageId === pageId
    )
      return;
    getSubMaterial();
    _subMaterialCalledRef.current = { id, pageId };

    // 根据统一的 isPassed 值设置是否可编辑
    setCanEdit(!isPassed);
  }, [id, pageId, getSubMaterial, isPassed]);

  useEffect(() => {
    setExtPrice(Number(detail?.saleOrder?.discount || 0));
    setCommission(Number(detail?.saleOrder?.commissionRate || 0));
  }, [detail]);

  // const { product, price } = useMemo(() => {
  //     let product: Product | null = null;
  //     let price: string = '';
  //     product = detail?.product || null;
  //     price = detail?.saleItem?.price || '';
  //     return {
  //         product,
  //         price
  //     }
  // }, [detail])

  const confirmCheck = async () => {
    if (detail) {
      setConfirmLoading(true);
      let netProfit = "";
      if (detail?.saleOrder) {
        // 净利润 = totalRealAmount - purchesePrice - (commission% * totalRealAmount) - extPrice
        // 使用 BigNumber 工具链计算并确保与 Number() 计算保持一致
        netProfit = minus(
          minus(
            minus(detail.saleOrder.totalRealAmount, purchesePrice + ""),
            divided(
              multi(commission + "", detail.saleOrder.totalRealAmount),
              "100"
            )
          ),
          extPrice + ""
        );
      }
      let saleOrderInfo: SaleOrderAdd = {
        ...detail.saleOrder,
        shippingMethod: detail?.saleOrder?.shippingMethod || '',
        otherNumber1: detail?.saleOrder?.otherNumber1 || '',
        mfgr: detail?.saleOrder?.mfgr || '',
        customerModel: detail?.saleOrder?.customerModel || '',
        urgencyLevel: detail?.saleOrder?.urgencyLevel || '',
        specialReq: detail?.saleOrder?.specialReq || '',
        extPrice: "",
        discount: extPrice + "",
        commissionRate: String(commission),
        bomCost: purchesePrice + "",
        netProfit
        // netProfit: detail?.saleItem ? '' + (Number(detail.saleItem.totalRealAmount) - Number(detail.saleItem?.bomCost || '') - (commission * Number(detail.saleItem.totalRealAmount) / 100) - extPrice) : undefined,
        // netProfit: detail?.saleItem ?
        //     numFormmater(Number(detail.saleItem.totalRealAmount) - purchesePrice - (commission * Number(detail.saleItem.totalRealAmount) / 100) - extPrice)
        //     : undefined,
      };

      try {
        let orderRes = await update(detail?.saleOrder?.id ?? "", saleOrderInfo);

        updateCb && updateCb({ ...detail, saleOrder: { ...orderRes } });

        notification.success({ placement: 'top', message: t("finance.saveSuccess") });
        setEditFlag(false);
      } catch (error) {
        console.log(error);
        notification.error({ placement: 'top', message: t("finance.saveError") });
      }
      const newQuos: QuotationRequest[] = FTR.current?.getSavedRows() ?? [];
      if (newQuos.length > 0) {
        let updates: Array<Promise<QuotationRequest>> = [];
        newQuos.forEach(item => {
          updates.push(
            quotationRequestUpdate(item.id, {
              standardPrice: item.standardPrice,
              standardCurrency: item.standardCurrency,
              exchangeRate: item.exchangeRate
            })
          );
        });
        await Promise.all(updates);
      }

      setConfirmLoading(false);
    }
  };

  const handleConfirm = () => {
    modal.confirm({
      title: t("finance.saveTip"),
      content: t("finance.saveTipContent"),
      onOk: () => confirmCheck()
    });
  };

  const PriceItems: DescriptionsProps["items"] = [
    {
      key: "amount",
      label: t("finance.salePrice"),
      children:
        detail && detail.saleOrder ? (
          <VerticalNumber
            value={numFormmater(Number(detail.saleOrder.totalRealAmount))}
            suffix={
              currencys.find(
                item => item.key === detail.saleOrder.currency
              )?.desc ?? ""
            }
          />
        ) : (
          ""
        ),
      span: 4
    },
    {
      key: "amount",
      label: "",
      children: "-",
      span: 2
    },
    {
      key: "purchesePrice",
      label: t("finance.maPrice"),
      children: <VerticalNumber value={numFormmater(purchesePrice)} />,
      span: 4
    },
    {
      key: "amount",
      label: "",
      children: "=",
      span: 2
    },
    {
      key: "amount",
      label: t("finance.finallyPrice"),
      children: detail?.saleOrder ? (
        <VerticalNumber
          value={numFormmater(
            Number(detail.saleOrder.totalRealAmount) - purchesePrice
          )}
        />
      ) : (
        ""
      ),
      span: 4
    }
  ];

  // 在小屏上把每一项都改为单行显示（包括分隔符），保持原始顺序
  const PriceItemsMobile: DescriptionsProps["items"] = [
    {
      key: "amount",
      label: t("finance.salePrice"),
      children:
        detail && detail.saleOrder ? (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div style={{ whiteSpace: "nowrap", fontSize: 14 }}>
              {numFormmater(Number(detail.saleOrder.totalRealAmount))}
              <span style={{ marginLeft: 8, fontSize: 12 }}>
                {currencys.find(
                  item => item.key === detail.saleOrder.currency
                )?.desc ?? ""}
              </span>
            </div>
          </div>
        ) : (
          ""
        ),
      span: 24
    },
    {
      key: "sep1",
      label: "",
      children: "-",
      span: 24
    },
    {
      key: "purchesePrice",
      label: t("finance.maPrice"),
      children: (
        <div style={{ fontSize: 14 }}>{numFormmater(purchesePrice)}</div>
      ),
      span: 24
    },
    {
      key: "sep2",
      label: "",
      children: "=",
      span: 24
    },
    {
      key: "amount_final",
      label: t("finance.finallyPrice"),
      children: detail?.saleOrder ? (
        <div style={{ fontSize: 14 }}>
          {numFormmater(
            Number(detail.saleOrder.totalRealAmount) - purchesePrice
          )}
        </div>
      ) : (
        ""
      ),
      span: 24
    }
  ];

  const CheckItems: DescriptionsProps["items"] = [
    {
      key: "commission",
      label: t("sale.commissionRate"),
      children: detail?.saleOrder ? (
        <InputNumber changeOnWheel
          style={{ width: "60%" }}
          min={0}
          max={100}
          value={commission}
          onChange={val => {
            setCommission(val || 0);
            setEditFlag(true);
          }}
          suffix="%"
          disabled={!canEdit}
          addonAfter={
            <div style={{ width: "60px" }}>
              {detail?.saleOrder && !!commission ? (
                <VerticalNumber
                  value={
                    (Number(detail.saleOrder.totalRealAmount) * commission) / 100
                  }
                />
              ) : (
                <VerticalNumber value={"0"} />
              )}
            </div>
          }
        />
      ) : (
        <span>0%</span>
      ),
      span: 6
    },
    {
      key: "extPrice",
      label: t("sale.discount"),
      children: detail?.saleOrder ? (
        <InputNumber changeOnWheel
          value={extPrice}
          min={0}
          max={Number(detail.saleOrder.realAmount)}
          placeholder="0"
          disabled={!canEdit}
          onChange={val => {
            setExtPrice(val ?? 0);
            setEditFlag(true);
          }}
          style={{ width: 140 }}
          controls={false}
          addonAfter={
            <Tooltip
              title={t("sale.discountHelp", {
                count: Number(detail?.saleOrder?.totalAmount || 0)
              })}
            >
              <QuestionCircleOutlined style={{ cursor: "pointer" }} />
            </Tooltip>
          }
        />
      ) : (
        <VerticalNumber value={"-0"} />
      ),
      span: 6
    },
    {
      key: "purePrice",
      label: t("finance.purePrice"),
      children: detail?.saleOrder ? (
        <VerticalNumber
          value={numFormmater(
            Number(detail.saleOrder.totalRealAmount) -
            purchesePrice -
            (commission * Number(detail.saleOrder.totalRealAmount)) / 100 -
            extPrice
          )}
        />
      ) : null,
      span: 3
    },
    {
      key: "confirmBtn",
      children: detail?.saleOrder ? (
        <Button
          type="primary"
          onClick={handleConfirm}
          loading={confirmLoading}
          disabled={!canEdit}
        >
          {t("finance.save")}
        </Button>
      ) : (
        <></>
      ),
      span: 1
    }
  ];

  const CheckItemsMobile: DescriptionsProps["items"] = [
    {
      key: "commission",
      label: t("sale.commissionRate"),
      children: detail?.saleOrder ? (
        <div>
          <InputNumber changeOnWheel
            style={{ width: "60%" }}
            min={0}
            max={100}
            value={commission}
            onChange={val => {
              setCommission(val || 0);
              setEditFlag(true);
            }}
            suffix="%"
            disabled={!canEdit}
          />
          <div style={{ marginTop: 6 }}>
            <div style={{ fontSize: 14 }}>
              {detail?.saleOrder && !!commission
                ? numFormmater(
                  (Number(detail.saleOrder.totalRealAmount) * commission) / 100
                )
                : numFormmater(0)}
            </div>
          </div>
        </div>
      ) : (
        <span>0%</span>
      ),
      span: 24
    },
    {
      key: "extPrice_mobile",
      label: t("sale.discount"),
      children: detail?.saleOrder ? (
        <div>
          <InputNumber changeOnWheel
            value={extPrice}
            min={0}
            max={Number(detail.saleOrder.realAmount)}
            placeholder="0"
            disabled={!canEdit}
            onChange={val => {
              setExtPrice(val ?? 0);
              setEditFlag(true);
            }}
            style={{ width: 140 }}
            controls={false}
            addonAfter={
              <Tooltip
                title={t("sale.discountHelp", {
                  count: Number(detail?.saleOrder?.totalAmount || 0)
                })}
              >
                <QuestionCircleOutlined style={{ cursor: "pointer" }} />
              </Tooltip>
            }
          />
        </div>
      ) : (
        <div style={{ fontSize: 14 }}>-0</div>
      ),
      span: 24
    },
    {
      key: "purePrice_mobile",
      label: t("finance.purePrice"),
      children: detail?.saleOrder ? (
        <div style={{ fontSize: 14 }}>
          {numFormmater(
            Number(detail.saleOrder.totalRealAmount) -
            purchesePrice -
            (commission * Number(detail.saleOrder.totalRealAmount)) / 100 -
            extPrice
          )}
        </div>
      ) : null,
      span: 24
    },
    {
      key: "confirmBtn_mobile",
      children: detail?.saleOrder ? (
        <Button
          type="primary"
          onClick={handleConfirm}
          loading={confirmLoading}
          disabled={!canEdit}
        >
          {t("finance.save")}
        </Button>
      ) : (
        <></>
      ),
      span: 24
    }
  ];

  const PriceItemsToRender = isMobile ? PriceItemsMobile : PriceItems;
  const CheckItemsToRender = isMobile ? CheckItemsMobile : CheckItems;

  return (
    <DetailModal
      logBookId={detail?.logBookId}
      modalProps={{
        width: "100vw",
        height: "100vh",
        styles: {
          content: { height: "100vh" },
          body: { height: "calc(100vh - 60px)", padding: 0 }
        },
        title: (
          <DetailTitle
            pageId={pageId}
            detail={detail}
            id={id}
            confirmValue={params?.confirmValue}
            handleOk={handleOk}
            onUpdatePurCount={() => setOpen(true)}
            items={items}
            passLoading={false}
            handlePurchase={() => handleCancel()}
          />
        ),
        onCancel: () => {
          if (editFlag) {
            modal.confirm({
              title: t("finance.costUpdate"),
              content: t("finance.costUpdateTip"),
              onOk: () => handleCancel()
            });
          } else handleCancel();
        },
        ...props
      }}
    >
      {loading ? (
        <Flex
          style={{ width: "100%", height: "100%" }}
          justify="center"
          align="center"
        >
          <Spin />
        </Flex>
      ) : (
        <>
          {params && (
            <RequirementSteps
              visible={open}
              onClose={() => setOpen(false)}
              id={id}
              order={detail?.saleOrder}
              onOk={() => {
                detail?.saleOrder && confirmCheck();
                handleOk && handleOk("del");
                setOpen(() => false);
              }}
              confirms={detail?.confirms ?? []}
            />
          )}
          <Flex vertical gap={20} style={{ height: "100%" }}>
            <RequirementReview confirms={detail?.confirms ?? []} isSHowLine />
            <RequirementDesc detail={detail} />
            <Descriptions items={PriceItemsToRender} size="small" column={24} />
            <Descriptions items={CheckItemsToRender} size="small" column={24} />
            {/* <Form<SaleOrderType> ref={formRef}>
                            {checkFormItems.map(item => <Form.Item {...item} />)}
                        </Form> */}
            <FinanceTable
              ref={FTR}
              detail={detail || null}
              detailId={detail?.id || ""}
              setpurchesePrice={setpurchesePrice}
              disabled={!canEdit}
              handleEdit={() => setEditFlag(true)}
            />
          </Flex>
        </>
      )}
    </DetailModal>
  );
};

export default FinanceDetail;
